/**
 * Convex-backed areas data layer.
 *
 * Unlike `convex-projects` / `convex-tasks`, there is **no snake_case shim
 * here**. Those shims exist only because the Supabase UI shapes outlived the
 * Supabase backend; areas are new, so they keep the Convex document shape all
 * the way to the component and there is nothing to translate.
 */
import { api } from '@/convex/_generated/api'
import type { Id } from '@/convex/_generated/dataModel'
import { convex, whenConvexReady } from '@/lib/convex-client'

export type AreaId = Id<'areas'>

/** A grouping label for the sidebar, not a tenant. See `convex/schema.ts`. */
export type AreaScope = 'personal' | 'work'

export interface Area {
  id: AreaId
  name: string
  color: string
  icon?: string
  scope?: AreaScope
  /** Absent means the user has not budgeted this area, which is not the same as zero. */
  weeklyTargetMinutes?: number
  position: number
  archived: boolean
}

type ConvexArea = {
  _id: AreaId
  name: string
  color: string
  icon?: string
  scope?: AreaScope
  weeklyTargetMinutes?: number
  position: number
  archived: boolean
}

function toArea(doc: ConvexArea): Area {
  return {
    id: doc._id,
    name: doc.name,
    color: doc.color,
    icon: doc.icon,
    scope: doc.scope,
    weeklyTargetMinutes: doc.weeklyTargetMinutes,
    position: doc.position,
    archived: doc.archived,
  }
}

export async function listAreas(includeArchived = false): Promise<Area[]> {
  await whenConvexReady()
  const docs = await convex.query(api.areas.list, { includeArchived })
  return docs.map(toArea)
}

export async function createArea(input: {
  name: string
  color?: string
  icon?: string
  scope?: AreaScope
  weeklyTargetMinutes?: number
}): Promise<Area> {
  await whenConvexReady()
  return toArea(await convex.mutation(api.areas.create, input))
}

export async function updateArea(
  id: AreaId,
  input: {
    name?: string
    color?: string
    icon?: string
    scope?: AreaScope
    /** `null` clears the weekly budget; omit to leave it alone. */
    weeklyTargetMinutes?: number | null
    archived?: boolean
  },
): Promise<Area> {
  await whenConvexReady()
  return toArea(await convex.mutation(api.areas.update, { areaId: id, ...input }))
}

export async function reorderAreas(orderedIds: AreaId[]): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.areas.reorder, { orderedIds })
}

export async function deleteArea(id: AreaId): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.areas.remove, { areaId: id })
}

/** Live areas subscription; mirrors `subscribeToProjects`. */
export function subscribeToAreas(
  onAreas: (areas: Area[]) => void,
  options?: { includeArchived?: boolean },
): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void whenConvexReady().then(() => {
    if (cancelled) return
    const watch = convex.watchQuery(api.areas.list, {
      includeArchived: options?.includeArchived ?? false,
    })
    unsubscribe = watch.onUpdate(() => {
      const docs = watch.localQueryResult()
      if (docs) onAreas(docs.map(toArea))
    })
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}
